import { useMemo } from 'react';
import { Modal, Tabs } from 'antd';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { RootState, isQuoteValid } from '@share/utils';
import { groupHotelByPackageDate } from '../../quotes/utils';
import { HotelTab } from './hotel-tab';
import { CondoTab } from './condo-tab';
import { CarTab } from './car-tab';
import { ActivityTab } from './activity-tab';

import './style.scss';

interface IProps {
  visible: boolean;
  quoteBase: string | null;
  onCancel: () => void;
}

const modalWidth = 1000;

export function QuotePackageModalCompare({ visible, quoteBase, onCancel }: IProps) {
  const loginStore = useSelector((state: RootState) => state.loginStore);
  const marginatorStore = useSelector((state: RootState) => state.marginatorStore);
  const quoteStore = useSelector((state: RootState) => state.quoteStore);

  const isHistorical = useMemo(() => {
    const { quotesPackages, quotesHotels, quotesCondos, quotesCars, quotesActivities } = quoteStore.result;
    return [...quotesPackages, ...quotesHotels, ...quotesCondos, ...quotesCars, ...quotesActivities]
      .some(q => q.quoteBaseId === quoteBase && q.isExpired);
  }, [quoteStore.result, quoteBase]);

  const quoteRecord = useMemo(() => {
    const { quotesPackages, quotesHotels, quotesCondos, quotesCars, quotesActivities } = quoteStore.result;

    // A genuine package (active or expired) already carries packageQuotes.
    const existingPackage = quotesPackages.find(p => p.quoteBaseId === quoteBase && p.packageQuotes);
    if (existingPackage) return existingPackage;

    // Otherwise assemble a package-shaped record from the standard quotes that share the
    // quoteBaseId, so this single modal renders hotels/condos/cars/activities for every tab uniformly.
    const pool = [...quotesHotels, ...quotesCondos, ...quotesCars, ...quotesActivities]
      .filter(q => q.quoteBaseId === quoteBase);
    if (!pool.length) return undefined;

    return {
      quoteBaseId: quoteBase,
      packageQuotes: {
        hotelQuotes: pool.filter(q => q.quoteType === 'Hotel' && !q.isCondo),
        condoQuotes: pool.filter(q => q.isCondo),
        carQuotes: pool.filter(q => q.quoteType === 'Car'),
        activityQuotes: pool.filter(q => q.quoteType === 'Activity'),
      },
    } as any;
  }, [quoteStore.result, quoteBase]);

  const { packageQuotes } = quoteRecord || {};
  const { hotelQuotes = [], condoQuotes = [], carQuotes = [], activityQuotes = [] } = packageQuotes || {};

  const formatLocationName = (name: string) => {
  if (!name) return 'unknown';

  return name
    .toLowerCase()     
    .trim()              
    .replace(/\s+/g, ' '); 
};

  const groupedHotels = useMemo(() => {
    const grouped = groupHotelByPackageDate(hotelQuotes);
    const byLocation: Record<string, any[]> = {};

    grouped.forEach((group: any) => {
      const firstHotel = group.items?.[0];
      let locationObj = null;
      try {
        locationObj = firstHotel?.location ? JSON.parse(firstHotel.location) : null;
      } catch (e) {
        locationObj = { name: firstHotel?.place || 'Unknown' };
      }

      const locKey = locationObj?.code || firstHotel?.place || 'unknown';
      if (!byLocation[locKey]) byLocation[locKey] = [];

      const adults = group.items?.[0]?.adults || 0;
      const kids = group.items?.[0]?.kids || 0;

      byLocation[locKey].push({
        ...group,
        guest: { adults, kids },
        items: group.items.map((q: any) => ({
          ...q,
          id: q.quoteBaseId,
          images: [{ url: q.image }],
          publicPrices: [{ price: q.publicPrice }],
          address: q.fullAddress,
          city: q.place,
          rating: { value: q.rating, reviewsCount: q.reviewsCount },
          amenities: (q.amenities || []).map((a: string) => ({ name: a })),
          startDate: q.checkIn,
          endDate: q.checkOut,
          isPast: !isQuoteValid(q),
          rooms: [],
          locationObj: locationObj,
        })),
      });
    });
    return byLocation;
  }, [hotelQuotes]);

  const groupedCondos = useMemo(() => {
    const byLocation: Record<string, any> = {};

    condoQuotes.forEach((q: any) => {
      let locationObj;
      try {
        locationObj = q.locationObj
          ? q.locationObj
          : q.location
          ? JSON.parse(q.location)
          : { name: q.place || 'Unknown' };
      } catch {
        locationObj = { name: q.place || 'Unknown' };
      }

      const locKey =
        locationObj?.code ||
        locationObj?.id ||
        q.place ||
        'unknown';

      if (!byLocation[locKey]) {
        byLocation[locKey] = {};
      }

      // ✅ Grouping keys
      const monthsKey = (q.months || []).join(',');
      const roomsDetailStr = q.roomsDetail || q.occupancy || '';
      
      const groupKey = q.searchType === "Flexible"
        ? (monthsKey ? `Flexible-${monthsKey}-${roomsDetailStr}` : `Flexible-Anytime-${roomsDetailStr}`)
        : `${q.checkIn}-${q.checkOut}-${roomsDetailStr}`;
      // ✅ Create group if not exists
      if (!byLocation[locKey][groupKey]) {
        byLocation[locKey][groupKey] = {
          locationObj,
          checkIn: q.checkIn,
          checkOut: q.checkOut,
          startDate: q.checkIn,
          endDate: q.checkOut,
          searchType: q.searchType,
          months: q.months || [],
          selectedMonths: q.months || [],
          adultsCount: q.adults || 0,
          kidsCount: q.kids || 0,
          bedroomsCount: q.bedrooms || 0,
          roomsDetail: roomsDetailStr,
          items: [],
        };
      }

      // ✅ Push item
      byLocation[locKey][groupKey].items.push({
        ...q,
        id: q.quoteBaseId,
        images: q.image ? [{ url: q.image }] : [],
        publicPrice: q.publicPrice,
        fullAddress: q.fullAddress,
        roomsDetail: roomsDetailStr,
        rating: {
          value: q.rating,
          reviewsCount: q.reviewsCount,
        },
        startDate: q.checkIn,
        endDate: q.checkOut,
        isPast: !isQuoteValid(q),
        locationObj,
      });
    });

    // ✅ Convert to final structure
    const finalResult: Record<string, any[]> = {};

    Object.keys(byLocation).forEach((locKey) => {
      finalResult[locKey] = Object.values(byLocation[locKey]);
    });

    return finalResult;
  }, [condoQuotes]);

  const groupedCars = useMemo(() => {
  const byLocation: Record<string, any> = {};

  carQuotes.forEach((q: any) => {
    const pickUp = formatLocationName(q.pickUpLocationDetail) || 'unknown';
    const dropOff = formatLocationName(q.dropOffLocationDetail) || 'unknown';

    const locationKey = `${pickUp}-${dropOff}`;

    if (!byLocation[locationKey]) {
      byLocation[locationKey] = {};
    }

    const groupKey = `${q.pickUpDate}-${q.dropOffDate}-${q.driverAge}-${q.countryISO}`;

    if (!byLocation[locationKey][groupKey]) {
      const pDate = moment(q.pickUpDate);
      const dDate = moment(q.dropOffDate);

      byLocation[locationKey][groupKey] = {
        pickUpLocationObj: { name: pickUp },
        dropOffLocationObj: { name: dropOff },
        startDate: pDate.isValid() ? pDate.format('YYYY-MM-DD') : '',
        endDate: dDate.isValid() ? dDate.format('YYYY-MM-DD') : '',
        pickUpTime: pDate.isValid() ? pDate.format('HH:mm') : '',
        returnTime: dDate.isValid() ? dDate.format('HH:mm') : '',
        driverAge: q.driverAge,
        countryISO: q.countryISO,
        items: [],
      };
    }

    const itemPDate = moment(q.pickUpDate);
    const itemDDate = moment(q.dropOffDate);

    byLocation[locationKey][groupKey].items.push({
      ...q,
      id: q.quoteBaseId,
      vehicle: {
        pictureURL: q.image,
        vehMakeModel: { name: q.displayName },
        transmissionType: q.features?.transmission || 'Automatic',
        passengerQuantity: q.features?.seats || 4,
        baggageQuantity: q.features?.luggage || 2,
      },
      vendor: { value: q.displayName },
      publicPrices: [{ price: q.publicPrice }],
      startDate: itemPDate.isValid() ? itemPDate.format('YYYY-MM-DD') : '',
      endDate: itemDDate.isValid() ? itemDDate.format('YYYY-MM-DD') : '',
      isPast: !isQuoteValid(q),
      pickUpLocationObj: { name: pickUp },
      dropOffLocationObj: { name: dropOff },
    });
  });

  const finalResult: Record<string, any[]> = {};

  Object.keys(byLocation).forEach((locKey) => {
    finalResult[locKey] = Object.values(byLocation[locKey]);
  });

  return finalResult;
}, [carQuotes]);

  const activityItems = useMemo(
    () => activityQuotes.map((q: any) => ({ ...q, isPast: !isQuoteValid(q) })),
    [activityQuotes]
  );

  const tabItems = useMemo(() => {
    const items = [];

    if (Object.keys(groupedHotels).length > 0) {
      items.push({
        key: 'hotels',
        label: <FormattedMessage id="hotels" />,
        children: (
          <HotelTab
            hotels={groupedHotels}
            marginatorStore={marginatorStore}
            loginStore={loginStore}
            onCancel={onCancel}
            isHistorical={isHistorical}
          />
        ),
      });
    }

    if (Object.keys(groupedCondos).length > 0) {
      items.push({
        key: 'condos',
        label: <FormattedMessage id="Condos" />,
        children: (
          <CondoTab
            condos={groupedCondos}
            marginatorStore={marginatorStore}
            loginStore={loginStore}
            condosessionKey={null}
            onCancel={onCancel}
            isHistorical={isHistorical}
          />
        ),
      });
    }

    if (Object.keys(groupedCars).length > 0) {
      items.push({
        key: 'cars',
        label: <FormattedMessage id="cars" />,
        children: (
          <CarTab
            cars={groupedCars}
            marginatorStore={marginatorStore}
            loginStore={loginStore}
            onCancel={onCancel}
            isHistorical={isHistorical}
          />
        ),
      });
    }

    if (activityItems.length > 0) {
      items.push({
        key: 'activities',
        label: <FormattedMessage id="activities" />,
        children: (
          <ActivityTab
            activities={activityItems}
            marginatorStore={marginatorStore}
            loginStore={loginStore}
            onCancel={onCancel}
            isHistorical={isHistorical}
          />
        ),
      });
    }

    return items;
  }, [groupedHotels, groupedCondos, groupedCars, activityItems, marginatorStore, loginStore, onCancel, isHistorical]);

  return (
    <Modal
      className="modal-compare quote-package-modal"
      open={visible}
      footer={null}
      onCancel={onCancel}
      width={modalWidth}
      destroyOnClose
    >
      <Tabs defaultActiveKey={tabItems[0]?.key} items={tabItems} className="quote-package-tabs" />
    </Modal>
  );
}
